"use client";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/components/ui/use-toast";
import { Skeleton } from "@/components/ui/skeleton";

const formSchema = z.object({
	name: z.string().min(2, {
		message: "Name must be at least 2 characters.",
	}),
	email: z.string().email({
		message: "Please enter a valid email address.",
	}),
	message: z.string().min(10, {
		message: "Message must be at least 10 characters.",
	}),
});

const ContactFormSkeleton = () => (
	<div className="space-y-4">
		<Skeleton className="h-10 w-full" />
		<Skeleton className="h-10 w-full" />
		<Skeleton className="h-32 w-full" />
		<Skeleton className="h-10 w-1/4" />
	</div>
);

const ContactForm = React.memo(() => {
	const [isSubmitting, setIsSubmitting] = useState(false);
	const { toast } = useToast();
	const [isLoaded, setIsLoaded] = useState(false);

	const form = useForm<z.infer<typeof formSchema>>({
		resolver: zodResolver(formSchema),
		defaultValues: {
			name: "",
			email: "",
			message: "",
		},
	});

	useEffect(() => {
		setIsLoaded(true);
	}, []);

	const onSubmit = async (values: z.infer<typeof formSchema>) => {
		console.log("Form submitted with values:", values);
		setIsSubmitting(true);

		try {
			console.log("Sending request to /api/contact");
			const response = await fetch("/api/contact", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(values),
			});

			console.log("Response received:", response.status);
			const data = await response.json();
			console.log("Response data:", data);

			if (response.ok) {
				console.log("Showing success toast");
				toast({
					title: "Message sent",
					description: data.message || "We'll get back to you soon!",
				});
				form.reset();
			} else {
				throw new Error(data.error || "Failed to send message");
			}
		} catch (error) {
			console.error("Error submitting form:", error);
			console.log("Showing error toast");
			toast({
				title: "Error",
				description:
					error instanceof Error
						? error.message
						: "Failed to send message. Please try again.",
				variant: "destructive",
			});
		} finally {
			setIsSubmitting(false);
		}
	};

	if (!isLoaded) {
		return <ContactFormSkeleton />;
	}

	return (
		<Form {...form}>
			<form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
				<FormField
					control={form.control}
					name="name"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Name</FormLabel>
							<FormControl>
								<Input placeholder="Your Name" {...field} autoComplete="name" />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				<FormField
					control={form.control}
					name="email"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Email</FormLabel>
							<FormControl>
								<Input
									placeholder="Your Email"
									{...field}
									autoComplete="email"
								/>
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				<FormField
					control={form.control}
					name="message"
					render={({ field }) => (
						<FormItem>
							<FormLabel>Message</FormLabel>
							<FormControl>
								<Textarea placeholder="Your Message" {...field} />
							</FormControl>
							<FormMessage />
						</FormItem>
					)}
				/>
				<Button type="submit" disabled={isSubmitting}>
					{isSubmitting ? "Sending..." : "Send Message"}
				</Button>
			</form>
		</Form>
	);
});

ContactForm.displayName = "ContactForm";

export default ContactForm;
